import { z } from "zod";
import { TRANSLATE_ANSWER_MAX, TRANSLATE_MAX_ITEMS } from "./translate.ts";

/**
 * Проверка перевода преподавателем: форма проверки, контракты трёх ручек и
 * чистые функции над ними. Проект: `docs/translate-review-design.md`.
 *
 * Живёт в корне пакета, а не за барьером: ответов и эталонов здесь нет ни в
 * одном поле. Проверку читают обе стороны (преподаватель пишет, ученик читает
 * возвращённую), а чистые функции нужны и серверу (перенос, готовность,
 * сверка смещений), и экрану (слова, итог «Верно N из M»). Бегунка тестов у
 * `@edu/web` нет, поэтому правила живут здесь.
 *
 * ⚠️ Смещения пометок в единицах строки JS (UTF-16) в тексте ответа ТОЙ
 * попытки, на которой стоит проверка (2.3 проекта).
 */

/* ────────────────────────────── потолки ────────────────────────────── */

/**
 * Потолки проверки (раздел 3 проекта).
 *
 *   REVIEW_MARKS_MAX     пометок на одно предложение
 *   REVIEW_FIX_MAX       знаков в исправлении одной пометки
 *   REVIEW_NOTE_MAX      знаков в пояснении одной пометки
 *   REVIEW_COMMENT_MAX   знаков в общем комментарии на всю работу
 */
export const REVIEW_MARKS_MAX = 10;
export const REVIEW_FIX_MAX = 500;
export const REVIEW_NOTE_MAX = 500;
export const REVIEW_COMMENT_MAX = 1000;
/**
 * Потолки черновика модели (2 и 5.3 `docs/translate-ai-check-design.md`).
 *
 *   REVIEW_DOUBT_MAX     знаков в сомнении модели («где смотреть и почему»)
 *   REVIEW_SUGGEST_MAX   знаков в исправленном предложении от модели
 */
export const REVIEW_DOUBT_MAX = 200;
export const REVIEW_SUGGEST_MAX = 600;

/* ─────────────────────────────── форма ─────────────────────────────── */

/**
 * Кто написал: преподаватель или модель. Модели сегодня нет (раздел 9
 * проекта), поле заведено сразу, чтобы черновик модели лёг тем же форматом.
 */
export const ReviewSource = z.enum(["teacher", "model"]);
export type ReviewSource = z.infer<typeof ReviewSource>;

/**
 * Пометка: место в ответе плюс необязательные исправление и пояснение (2.5).
 * `start` включительно, `end` исключительно.
 */
export const ReviewMark = z.object({
  id: z.string().min(1).max(64),
  start: z.number().int().nonnegative(),
  end: z.number().int().positive(),
  fix: z.string().max(REVIEW_FIX_MAX).nullable(),
  note: z.string().max(REVIEW_NOTE_MAX).nullable(),
  source: ReviewSource,
});
export type ReviewMark = z.infer<typeof ReviewMark>;

/**
 * Вердикт на предложение (2.6). `null` это «ещё не решено», как и отсутствие
 * ключа. `empty` («нет ответа») ставит сервер сам, руками он не меняется.
 */
export const ReviewVerdict = z.enum(["ok", "wrong", "empty"]);
export type ReviewVerdict = z.infer<typeof ReviewVerdict>;

/**
 * Предложение проверки.
 *
 * Четыре необязательных поля черновика модели (2
 * `docs/translate-ai-check-design.md`). Ученику не уезжает ни одно: возврат
 * их стирает (`reviewForStudent`), а черновик ученику не виден вовсе.
 *
 * - `by`: кто решил вердикт. У решения модели `"model"`, пока преподаватель
 *   его не подтвердил или не поправил. Модель заполняет только предложение,
 *   где нет ни вердикта, ни пометок, ни `by` (1.2)
 * - `unsure`: модель не уверена. Вердикта и пометок с исправлением тогда нет,
 *   есть `doubt`. Пометки с одним пояснением преподаватель ставить вправе
 * - `doubt`: одно предложение для преподавателя, где смотреть и почему
 * - `suggest`: предложение целиком так, как написала бы его модель («Как
 *   написал бы ИИ», 5.3). Только для показа преподавателю
 *
 * ⚠️ Поля добавлены сюда и в `itemKey` веба ОДНИМ изменением (2 проекта):
 * иначе сравнение черновиков их не видит.
 */
export const ReviewItem = z.object({
  verdict: ReviewVerdict.nullable(),
  marks: z.array(ReviewMark).max(REVIEW_MARKS_MAX),
  by: ReviewSource.optional(),
  unsure: z.boolean().optional(),
  doubt: z.string().max(REVIEW_DOUBT_MAX).nullable().optional(),
  suggest: z.string().max(REVIEW_SUGGEST_MAX).nullable().optional(),
});
export type ReviewItem = z.infer<typeof ReviewItem>;

/**
 * Проверка целиком (раздел 3). Ключи `items` это id предложений задания;
 * какие именно, схема не знает, это сверяет `reviewContentProblem` по
 * элементу. Потолок числа ключей обязателен по той же причине, что у ответа:
 * лишний ключ ронял бы разбор всей выдачи.
 */
export const ReviewContent = z.object({
  items: z
    .record(z.string().min(1), ReviewItem)
    .refine((items) => Object.keys(items).length <= TRANSLATE_MAX_ITEMS),
  comment: z.string().max(REVIEW_COMMENT_MAX).nullable(),
  source: ReviewSource,
});
export type ReviewContent = z.infer<typeof ReviewContent>;

/** Ответ попытки по предложениям. Ключа нет: предложение не заполнено. */
export type ReviewLines = Readonly<Record<string, string>>;

/* ───────────────────────────── контракты ───────────────────────────── */

/**
 * Попытка, на которой стоит проверка. `lines` NULL это «ответ стёрт по сроку»:
 * у непроверенного перевода старше 30 суток (2.11), и тогда проверять нечего.
 */
export const ReviewAttempt = z.object({
  attemptId: z.string().uuid(),
  lines: z.record(z.string(), z.string().max(TRANSLATE_ANSWER_MAX)).nullable(),
  createdAt: z.number().int(),
});
export type ReviewAttempt = z.infer<typeof ReviewAttempt>;

/** Черновик преподавателя. `updatedAt` это предусловие следующей записи. */
export const ReviewDraft = z.object({
  attemptId: z.string().uuid(),
  content: ReviewContent,
  updatedAt: z.number().int(),
});
export type ReviewDraft = z.infer<typeof ReviewDraft>;

/** Возвращённая проверка вместе со своей попыткой. Неизменяема (2.9). */
export const ReturnedReview = z.object({
  attempt: ReviewAttempt,
  content: ReviewContent,
  returnedAt: z.number().int(),
});
export type ReturnedReview = z.infer<typeof ReturnedReview>;

/**
 * Состояние проверки одного перевода. Ответ `GET /v1/homework/:id/reviews` по
 * строке на задание и тело отказа `review_stale` (поле `current`).
 *
 * ⚠️ `latest`, `previous` и `draft` едут ТОЛЬКО преподавателю: у ученика этих
 * полей нет вовсе, а не `null`. Черновик ученику не виден никаким путём (1.1).
 *
 * - `latest`: последняя зачётная попытка ученика, `null` если попыток нет
 * - `previous`: попытка непосредственно перед ней. Нужна экрану «ученик
 *   прислал исправленный вариант»: прошлая версия изменённых предложений рядом
 *   (5.1 проекта), в том числе когда прошлая попытка не была возвращена
 * - `draft`: черновик, всегда на `latest` (2.2)
 * - `returned`: возвращённые проверки, свежие первыми
 */
/**
 * Состояние задачи черновика модели для ПОСЛЕДНЕЙ попытки (`review_job`, 2
 * `docs/translate-ai-check-design.md`).
 *
 *   queued, running   строка «ИИ проверяет работу» и опрос раз в 5 с (4.4)
 *   done              черновик записан (или отброшен как устаревший)
 *   skipped           черновик не готовился, причина в `reason`
 *   failed            три сбоя подряд, `reason` это `error`
 */
export const ReviewCheckStatus = z.enum(["queued", "running", "done", "skipped", "failed"]);
export type ReviewCheckStatus = z.infer<typeof ReviewCheckStatus>;

/**
 * Почему черновик модели не готовился.
 *
 *   credits    кредиты ИИ на месяц кончились
 *   stale      пришла новая попытка
 *   cap        потолок платных проверок на задание (10)
 *   erased     ответ стёрт по сроку
 *   voided     задание аннулировано
 *   trash      домашка в корзине
 *   returned   проверка уже возвращена
 *   nothing    проверять нечего: всё решено преподавателем или перенесено
 *   error      сбой вызова модели после трёх попыток
 */
export const ReviewCheckReason = z.enum([
  "credits",
  "stale",
  "cap",
  "erased",
  "voided",
  "trash",
  "returned",
  "nothing",
  "error",
]);
export type ReviewCheckReason = z.infer<typeof ReviewCheckReason>;

export const ReviewCheck = z.object({
  status: ReviewCheckStatus,
  reason: ReviewCheckReason.nullable(),
});
export type ReviewCheck = z.infer<typeof ReviewCheck>;

export const ReviewExerciseState = z.object({
  elementId: z.string().min(1),
  /** Не null: задание аннулировано. Запись и возврат его не трогают (2.12). */
  voidedAt: z.number().int().nullable(),
  latest: ReviewAttempt.nullable().optional(),
  previous: ReviewAttempt.nullable().optional(),
  draft: ReviewDraft.nullable().optional(),
  /**
   * Задача черновика модели для последней попытки, `null` если её нет (модель
   * выключена, попытка до её включения). ⚠️ Только преподавателю, у ученика
   * поля нет вовсе, как у `draft`.
   */
  check: ReviewCheck.nullable().optional(),
  returned: z.array(ReturnedReview),
});
export type ReviewExerciseState = z.infer<typeof ReviewExerciseState>;

export const HomeworkReviewsResponse = z.object({
  /** Только переводы, в порядке заданий в работе. */
  exercises: z.array(ReviewExerciseState),
});
export type HomeworkReviewsResponse = z.infer<typeof HomeworkReviewsResponse>;

/**
 * Тело `PUT /v1/homework/:homeworkId/exercises/:elementId/review`: черновик
 * целиком.
 *
 * - `attemptId`: на какой попытке преподаватель смотрит;
 * - `updatedAt`: какую версию черновика правит, `null` если черновика ещё нет.
 *
 * Не та попытка или не та версия: 409 `review_stale` с `current` в деталях.
 */
export const SaveReviewRequest = z.object({
  attemptId: z.string().uuid(),
  updatedAt: z.number().int().nullable(),
  content: ReviewContent,
});
export type SaveReviewRequest = z.infer<typeof SaveReviewRequest>;

export const SaveReviewResponse = z.object({ draft: ReviewDraft });
export type SaveReviewResponse = z.infer<typeof SaveReviewResponse>;

/** Детали отказа `review_stale`: текущее состояние задания. */
export const ReviewStaleDetails = z.object({ current: ReviewExerciseState });
export type ReviewStaleDetails = z.infer<typeof ReviewStaleDetails>;

/**
 * Тело `POST /v1/homework/:homeworkId/reviews/return` (2.7 проекта).
 *
 * `restOk`: задания, у которых нерешённые предложения считаются верными
 * («Остальные N верно, вернуть»). ⚠️ С попыткой, которую преподаватель видел:
 * если ученик тем временем прислал новую, «остальные верно» легло бы на
 * предложения, которых преподаватель не читал, и задание уходит в `notReady`
 * с причиной `stale`. Черновика у такого задания может ещё не быть: «всё верно»
 * одним нажатием без единой пометки это обычный случай.
 */
/**
 * Итог проверки ответа текстом (1.9 `docs/homework-scratch-design.md`):
 * «Принято» (`ok`) или «Исправить» (`fix`). Живёт здесь, а не в модуле ответа
 * текстом, потому что возврат общий с переводом и его контракт стоит ниже.
 */
export const AnswerReviewOutcome = z.enum(["ok", "fix"]);
export type AnswerReviewOutcome = z.infer<typeof AnswerReviewOutcome>;

/**
 * Возврат ответа текстом с экрана его проверки (1.9): какой ответ
 * преподаватель видел (`answerAt`), какую версию черновика (`updatedAt`,
 * `null` если черновика нет) и итог. Не тот ответ или не та версия: 409
 * `review_stale` на весь возврат, и не уходит ничего, в том числе переводы:
 * смешанная домашка ученику частями не уезжает.
 */
export const ReturnAnswerRequest = z.object({
  answerAt: z.number().int(),
  updatedAt: z.number().int().nullable(),
  outcome: AnswerReviewOutcome,
});
export type ReturnAnswerRequest = z.infer<typeof ReturnAnswerRequest>;

export const ReturnReviewsRequest = z.object({
  restOk: z
    .array(z.object({ elementId: z.string().min(1).max(200), attemptId: z.string().uuid() }))
    .max(50)
    .default([]),
  /**
   * Ответ текстом (1.9). Без поля черновик ответа, если он есть, уходит вместе
   * с переводами, когда итог в нём уже выбран, и называется неготовым, когда
   * нет.
   */
  answer: ReturnAnswerRequest.optional(),
});
export type ReturnReviewsRequest = z.infer<typeof ReturnReviewsRequest>;

/**
 * Что вернулось и что нет. Возврат идёт по ДОМАШКЕ: все готовые черновики её
 * переводов разом; неготовый не блокирует готовый, аннулированное задание не
 * участвует и в ответе не называется.
 *
 *   undecided   есть нерешённые предложения, их число в `undecided`
 *   stale       `restOk` пришёл на попытку, которая уже не последняя
 *   erased      ответ стёрт по сроку, проверять нечего (только для `restOk`)
 *   unsure      `restOk` на задание, где модель не уверена хотя бы в одном
 *               предложении: «остальные верно» записало бы «верно» туда, куда
 *               преподаватель не смотрел (2 `docs/translate-ai-check-design.md`).
 *               В `undecided` число нерешённых, сомнения среди них
 */
export const ReturnReviewsResponse = z.object({
  returned: z.array(
    z.object({
      elementId: z.string().min(1),
      attemptId: z.string().uuid(),
      returnedAt: z.number().int(),
    }),
  ),
  notReady: z.array(
    z.object({
      elementId: z.string().min(1),
      reason: z.enum(["undecided", "stale", "erased", "unsure"]),
      undecided: z.number().int().nonnegative(),
    }),
  ),
  /**
   * Ответ текстом (1.9). Поля нет: возвращать было нечего (ни черновика, ни
   * `answer` в запросе). `not_ready`: черновик есть, а вернуть его нельзя,
   * причина в `reason`.
   */
  answer: z
    .discriminatedUnion("status", [
      z.object({
        status: z.literal("returned"),
        answerAt: z.number().int(),
        outcome: AnswerReviewOutcome,
        returnedAt: z.number().int(),
      }),
      /*
       * `undecided`: итог в черновике не выбран. `answer_erased`: ответ стёрт
       * по сроку, пока черновик ждал, возвращать нечего
       */
      z.object({ status: z.literal("not_ready"), reason: z.enum(["undecided", "answer_erased"]) }),
    ])
    .optional(),
});
export type ReturnReviewsResponse = z.infer<typeof ReturnReviewsResponse>;

/* ─────────────────────────── слова ответа ─────────────────────────── */

export interface ReviewWord {
  start: number;
  end: number;
  text: string;
}

/**
 * Ответ по словам для выделения (2.4 проекта). Слово это `\S+`: пунктуация
 * прилипает к слову («Haus,»), апостроф и дефис внутри слова его не рвут.
 * Смещения в единицах строки JS, те же, что у пометок.
 *
 * Суррогатная пара пробелом не бывает, поэтому слово её не разрежет: граница
 * слова всегда стоит на пробельном знаке или на краю строки.
 */
export function reviewWords(text: string): ReviewWord[] {
  const out: ReviewWord[] = [];
  for (const match of text.matchAll(/\S+/g)) {
    const start = match.index;
    out.push({ start, end: start + match[0].length, text: match[0] });
  }
  return out;
}

/* ───────────────────────────── правила ───────────────────────────── */

/**
 * Пометка с исправлением ставит предложению «неверно» сама, пометка только с
 * пояснением вердикт не меняет (2.5): у Даши обычное «верно, но можно и
 * lernt». Исправление из одних пробелов исправлением не считается.
 */
export function markSetsWrong(mark: Pick<ReviewMark, "fix">): boolean {
  return mark.fix !== null && mark.fix.trim() !== "";
}

/**
 * Поставить или заменить пометку (по `id`), держа порядок по `start`.
 *
 * ⚠️ «Неверно» ставится только когда исправление ПОЯВЛЯЕТСЯ (новая пометка с
 * ним или прежняя без него). Правка пояснения у пометки, где исправление уже
 * было, вердикт не трогает: преподаватель вправе переключить его руками в
 * любую сторону, и следующая правка текста не должна молча отменять его выбор.
 */
export function applyMark(item: ReviewItem, mark: ReviewMark): ReviewItem {
  const prev = item.marks.find((m) => m.id === mark.id);
  const marks = [...item.marks.filter((m) => m.id !== mark.id), mark].sort(
    (a, b) => a.start - b.start,
  );
  const fixAppeared = markSetsWrong(mark) && (prev === undefined || !markSetsWrong(prev));
  return { verdict: fixAppeared ? "wrong" : item.verdict, marks };
}

/** Убрать пометку. Вердикт остаётся: его снимает человек, а не удаление. */
export function removeMark(item: ReviewItem, markId: string): ReviewItem {
  return { verdict: item.verdict, marks: item.marks.filter((m) => m.id !== markId) };
}

/**
 * Проверка в том виде, в каком её получит ученик (2
 * `docs/translate-ai-check-design.md`): «Вернуть ученику» делает её проверкой
 * преподавателя. У всех пометок `source: "teacher"`, поля черновика модели
 * (`by`, `unsure`, `doubt`, `suggest`) удалены. Ученик не видит ни «ИИ», ни
 * сомнений модели.
 */
export function reviewForStudent(content: ReviewContent): ReviewContent {
  const items: Record<string, ReviewItem> = {};
  for (const [id, item] of Object.entries(content.items)) {
    items[id] = {
      verdict: item.verdict,
      marks: item.marks.map((mark) => ({ ...mark, source: "teacher" })),
    };
  }
  return { items, comment: content.comment, source: "teacher" };
}

/** Есть ли предложение, в котором модель не уверена. */
export function reviewHasUnsure(content: ReviewContent): boolean {
  return Object.values(content.items).some((item) => item.unsure === true);
}

/** Пустая проверка: ни одного решения, без комментария. */
export function emptyReviewContent(source: ReviewSource = "teacher"): ReviewContent {
  return { items: {}, comment: null, source };
}

/**
 * «Нет ответа» у каждого предложения, чьего ключа нет в ответе (2.6): ставит
 * сервер сам, пометок у такого предложения нет. Ключи предложений, которых
 * нет в задании, отбрасываются.
 */
export function withEmptyVerdicts(
  content: ReviewContent,
  itemIds: readonly string[],
  lines: ReviewLines,
): ReviewContent {
  const items: Record<string, ReviewItem> = {};
  for (const id of itemIds) {
    if (!Object.hasOwn(lines, id)) {
      items[id] = { verdict: "empty", marks: [] };
      continue;
    }
    const item = content.items[id];
    if (item !== undefined) items[id] = item;
  }
  return { ...content, items };
}

/** Лежит ли смещение посреди суррогатной пары. */
function splitsPair(text: string, at: number): boolean {
  if (at <= 0 || at >= text.length) return false;
  const before = text.charCodeAt(at - 1);
  const after = text.charCodeAt(at);
  return before >= 0xd800 && before <= 0xdbff && after >= 0xdc00 && after <= 0xdfff;
}

/**
 * Серверная сверка проверки с заданием и ответом попытки (2.3 и 2.6). `null`
 * это «всё в порядке», иначе первая найденная беда словами для лога.
 *
 * - ключи только из предложений задания;
 * - предложение без ответа: вердикт пуст или «нет ответа», пометок нет;
 * - предложение с ответом: «нет ответа» руками не ставится; пометки внутри
 *   текста (`0 ≤ start < end ≤ длина`), по порядку, без пересечений, не
 *   посреди суррогатной пары, с разными `id`;
 * - «не уверена» (`unsure`) только у предложения с ответом, без вердикта,
 *   без пометок с исправлением (пометки с одним пояснением можно: заметка
 *   преподавателя на сомнительном предложении), с непустым `doubt`; `doubt`
 *   без `unsure` не бывает.
 */
export function reviewContentProblem(
  content: ReviewContent,
  itemIds: readonly string[],
  lines: ReviewLines,
): string | null {
  const known = new Set(itemIds);
  for (const [id, item] of Object.entries(content.items)) {
    if (!known.has(id)) return `предложения ${id} нет в задании`;
    const line = Object.hasOwn(lines, id) ? lines[id] : undefined;

    const doubt = item.doubt ?? null;
    if (item.unsure === true) {
      if (line === undefined) return `у предложения ${id} нет ответа, сомнения быть не может`;
      if (item.verdict !== null) return `у предложения ${id} сомнение и вердикт сразу`;
      // Пометка с одним пояснением сомнению не мешает: это заметка
      // преподавателя, вердикта она не ставит (2.5). Исправление ставит
      // «неверно», то есть сомнение уже разрешено
      if (item.marks.some(markSetsWrong)) return `у предложения ${id} сомнение и исправление сразу`;
      if (doubt === null || doubt.trim() === "") return `у предложения ${id} сомнение без пояснения`;
    } else if (doubt !== null) {
      return `у предложения ${id} пояснение сомнения без сомнения`;
    }

    if (line === undefined) {
      if (item.verdict !== null && item.verdict !== "empty") {
        return `у предложения ${id} нет ответа, вердикт ${item.verdict} невозможен`;
      }
      if (item.marks.length > 0) return `у предложения ${id} нет ответа, пометок быть не может`;
      continue;
    }

    if (item.verdict === "empty") return `у предложения ${id} есть ответ, «нет ответа» невозможно`;
    const problem = marksProblem(item.marks, line);
    if (problem !== null) return problem;
  }
  return null;
}

/**
 * Сверка пометок ОДНОГО элемента (предложения перевода, абзаца ответа текстом)
 * с его текстом: внутри текста (`0 ≤ start < end ≤ длина`), по порядку, без
 * пересечений, не посреди суррогатной пары, с разными `id`. `null`: всё в
 * порядке, иначе первая беда словами для лога.
 *
 * Одна на перевод и на ответ текстом (1.9 `docs/homework-scratch-design.md`):
 * правило смещений у них общее, и второе его место однажды разошлось бы.
 */
export function marksProblem(marks: readonly ReviewMark[], line: string): string | null {
  const ids = new Set<string>();
  let prevEnd = 0;
  for (const mark of marks) {
    if (ids.has(mark.id)) return `повтор пометки ${mark.id}`;
    ids.add(mark.id);
    if (mark.start >= mark.end || mark.end > line.length) {
      return `пометка ${mark.id} вне текста ответа`;
    }
    if (mark.start < prevEnd) return `пометка ${mark.id} не по порядку или пересекается`;
    if (splitsPair(line, mark.start) || splitsPair(line, mark.end)) {
      return `пометка ${mark.id} режет суррогатную пару`;
    }
    prevEnd = mark.end;
  }
  return null;
}

/* ─────────────────────── готовность и итог ─────────────────────── */

export interface ReviewProgress {
  /** Предложений в задании. */
  total: number;
  /** Со словом «верно». */
  ok: number;
  /** Ещё не решённые, по порядку задания. */
  undecided: string[];
}

/**
 * Готовность и итог «Верно N из M» (2.7). Считается по предложениям задания,
 * отсутствующий ключ это «не решено». «Нет ответа» решённым считается.
 */
export function reviewProgress(content: ReviewContent, itemIds: readonly string[]): ReviewProgress {
  let ok = 0;
  const undecided: string[] = [];
  for (const id of itemIds) {
    const verdict = content.items[id]?.verdict ?? null;
    if (verdict === null) undecided.push(id);
    else if (verdict === "ok") ok += 1;
  }
  return { total: itemIds.length, ok, undecided };
}

/** Всё ли решено: кнопка «Вернуть ученику» вместо «Остальные N верно». */
export function reviewComplete(content: ReviewContent, itemIds: readonly string[]): boolean {
  return reviewProgress(content, itemIds).undecided.length === 0;
}

/**
 * «Остальные N верно» (2.7): каждое нерешённое предложение с ответом
 * становится верным, пометки при нём сохраняются (пояснение без исправления
 * стоит и на верном). Предложение без ответа получает «нет ответа».
 */
export function fillRestOk(
  content: ReviewContent,
  itemIds: readonly string[],
  lines: ReviewLines,
): ReviewContent {
  const filled = withEmptyVerdicts(content, itemIds, lines);
  const items: Record<string, ReviewItem> = { ...filled.items };
  for (const id of itemIds) {
    const item = items[id];
    if (item === undefined) items[id] = { verdict: "ok", marks: [] };
    // Сомнение модели «верно» не закрывает: сервер такой возврат отвергает
    // (`unsure` в `ReturnReviewsResponse`), а здесь оно остаётся нерешённым
    else if (item.verdict === null && item.unsure !== true) items[id] = { ...item, verdict: "ok" };
  }
  return { ...filled, items };
}

/** Есть ли в проверке что исправлять: «неверно» или «нет ответа». */
export function reviewHasFixes(content: ReviewContent): boolean {
  return Object.values(content.items).some(
    (item) => item.verdict === "wrong" || item.verdict === "empty",
  );
}

/* ─────────────────────────── перенос ─────────────────────────── */

/**
 * Перенос решений на новую попытку, одно правило на все случаи (2.8).
 *
 * Источник это проверка НЕПОСРЕДСТВЕННО предыдущей попытки, черновик или
 * возвращённая. Предложение, чей текст совпал байт в байт (без `trim`),
 * получает вердикт и пометки источника целиком: смещения в том же тексте
 * верны. Изменённое ждёт решения, удалённое становится «нет ответа»,
 * заполненное вместо пустого ждёт решения. Это же правило делает безвредным
 * то, что сервер принимает от ученика любые строки: подменённое «верное»
 * предложение теряет «верно».
 *
 * `keepComment`: общий комментарий переезжает только с черновиком (это
 * недописанная работа преподавателя). У возвращённой проверки он уже доставлен
 * ученику, и в новой проверке его повтор читался бы как новый.
 *
 * `prevLines` NULL (ответ прошлой попытки стёрт) означает «сверять не с чем»:
 * ни одно предложение не переносится.
 *
 * Предложение переносится ЦЕЛИКОМ, вместе с полями черновика модели (`by`,
 * `unsure`, `doubt`, `suggest`): неизменённое предложение с решением или
 * сомнением модели повторно не отправляется (3, шаг 5
 * `docs/translate-ai-check-design.md`), и пометка «ИИ» на нём остаётся, пока
 * преподаватель её не снимет.
 */
export function carryReview(args: {
  source: ReviewContent;
  prevLines: ReviewLines | null;
  nextLines: ReviewLines;
  itemIds: readonly string[];
  keepComment: boolean;
}): ReviewContent {
  const { source, prevLines, nextLines, itemIds } = args;
  const items: Record<string, ReviewItem> = {};
  for (const id of itemIds) {
    const next = Object.hasOwn(nextLines, id) ? nextLines[id] : undefined;
    if (next === undefined) continue;
    const prev = prevLines !== null && Object.hasOwn(prevLines, id) ? prevLines[id] : undefined;
    const item = source.items[id];
    if (prev !== undefined && prev === next && item !== undefined) items[id] = item;
  }
  return withEmptyVerdicts(
    {
      items,
      comment: args.keepComment ? source.comment : null,
      source: source.source,
    },
    itemIds,
    nextLines,
  );
}
